import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  WarningFilled,
} from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Popover,
  Row,
  Spin,
  notification,
} from 'antd'
import { useState } from 'react'
import './AddPropertyModal.style.less'
import { useMutation, useQuery } from 'react-query'
import {
  createProperty,
  deletePropertyValue,
  getPropertyDetail,
  savePropertyValue,
  updateProperty,
  updatePropertyValue,
} from '../../../DataManagement.transform'
import { camelCase } from 'lodash'
import { queryClient } from '@/config/react-query'
import { isEmptyValue } from '@/utils/common'

interface Props {
  visible: boolean
  onClose: () => void
  data: any
}

function AddPropertyModal(props: Props) {
  const [propertyValues, setPropertyValues] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const { isLoading, refetch } = useQuery(
    'property_values',
    async () => {
      const tmpData = await getPropertyDetail(props.data?.id)
      setPropertyValues(
        tmpData?.property_values.map((item) => {
          return {
            ...item,
            isEdit: false,
          }
        })
      )
      return tmpData
    },
    {
      enabled: !!props.data,
    }
  )

  const mutation = useMutation(async (data: any) => {
    await createProperty(data)
  })

  const updateMutation = useMutation(async (data: any) => {
    await updateProperty(props.data.id, data)
  })

  const deleteMutation = useMutation(async (id: string) => {
    await deletePropertyValue(id)
  })

  const propertyValueMutation = useMutation(async (value: any) => {
    await savePropertyValue(props.data.id, props.data.name, value)
  })

  const updatePropertyValueMutation = useMutation(async (value: any) => {
    await updatePropertyValue(value.id, value.value)
  })

  const onSaveProperty = async (values) => {
    if (isEmptyValue(props.data)) {
      const payload = {
        name: camelCase(values.name),
        title: values.name,
      }
      await mutation.mutateAsync(payload)
    } else {
      const payload = {
        id: props.data.id,
        name: props.data?.name,
        title: values.name,
      }
      await updateMutation.mutateAsync(payload)
    }
    await queryClient.refetchQueries(['system-config-property'])
    props.onClose()
  }

  // const FIELD_INPUT_TYPE_ARRAY = [
  //   'text',
  //   'number',
  //   'select',
  //   'datetime',
  //   // 'file',
  // ]

  const onAddValue = () => {
    setPropertyValues([...propertyValues, { value: '', isEdit: true }])
  }

  const onValueChange = (index, value: any) => {
    const tmp = [...propertyValues]
    tmp[`${index}`].value = value
    setPropertyValues(tmp)
  }

  const onSaveChange = async (index) => {
    const tmp = [...propertyValues]
    if (isEmptyValue(tmp[`${index}`].value)) {
      notification.error({
        message: 'cannot save empty value',
        duration: 3,
        placement: 'top',
      })
      return
    }
    tmp[`${index}`].isEdit = false
    if (!isEmptyValue(tmp[`${index}`].id)) {
      await updatePropertyValueMutation.mutateAsync(tmp[`${index}`])
    } else {
      await propertyValueMutation.mutateAsync(tmp[`${index}`].value)
    }
    await refetch()
    setPropertyValues(tmp)
  }

  const onEdit = (index) => {
    const tmp = [...propertyValues]
    tmp[`${index}`].isEdit = true
    setPropertyValues(tmp)
  }

  const onRemovePropertyValue = async () => {
    const tmp = [...propertyValues]
    if (!isEmptyValue(tmp[selectedIndex].id)) {
      await deleteMutation.mutateAsync(tmp[selectedIndex].id)
      await refetch()
    } else {
      tmp.splice(selectedIndex, 1)
      setPropertyValues(tmp)
    }
    setSelectedIndex(-1)
  }

  const content = (
    <div className="content-popmenu-delete">
      <p>Do you want to delete this item?</p>
      <Row justify={'center'}>
        <Button onClick={() => setSelectedIndex(-1)}>No</Button>
        <Button onClick={onRemovePropertyValue} type="primary">
          Yes
        </Button>
      </Row>
    </div>
  )

  return (
    <Modal
      className="modal-add-property-container"
      open={props.visible}
      width={450}
      footer={false}
      maskClosable={false}
      onCancel={props.onClose}
    >
      <div>
        <p className="title">{props.data ? 'Edit' : 'New'} Property</p>
        <Form labelCol={{ span: 7 }} onFinish={onSaveProperty}>
          <Form.Item
            label="Name"
            name="name"
            initialValue={props.data?.title}
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input size="large" placeholder="Text" />
          </Form.Item>
          {/* <Form.Item name="dataType" label="Data Type">
            <Select
              placeholder="Select"
              options={FIELD_INPUT_TYPE_ARRAY.map((item) => {
                return {
                  label: item,
                  value: item,
                }
              })}
            />
          </Form.Item> */}
          {isLoading && <Spin />}
          {!isEmptyValue(props.data) && (
            <>
              <p className="list-option-label">List Options:</p>
              <div>
                {propertyValues.map((item, index) => {
                  const { id } = item
                  const optionKey = `option-${id}`

                  if (item.isEdit)
                    return (
                      <Row
                        gutter={16}
                        className="property-values-row-input"
                        key={optionKey}
                      >
                        <Col span={19}>
                          <Input
                            onChange={(e) =>
                              onValueChange(index, e.target.value)
                            }
                            size="large"
                            value={item.value}
                            placeholder="Text"
                          />
                        </Col>
                        <Col span={2}>
                          {!isEmptyValue(item.value) ? (
                            <Button
                              onClick={() => onSaveChange(index)}
                              type="primary"
                              size="large"
                            >
                              Save
                            </Button>
                          ) : (
                            <Popover
                              open={selectedIndex == index}
                              content={content}
                              trigger={'click'}
                            >
                              <Button
                                onClick={() => setSelectedIndex(index)}
                                type="primary"
                                size="large"
                              >
                                Cancel
                              </Button>
                            </Popover>
                          )}
                        </Col>
                      </Row>
                    )
                  return (
                    <Row key={optionKey} className="property-values-row">
                      <p className="name">{item.value}</p>
                      <Button
                        onClick={() => onEdit(index)}
                        icon={<EditOutlined />}
                      />
                      <Popover
                        open={selectedIndex == index}
                        content={content}
                        trigger={'click'}
                      >
                        <Button
                          onClick={() => {
                            setSelectedIndex(index)
                          }}
                          icon={<DeleteOutlined />}
                        />
                      </Popover>
                    </Row>
                  )
                })}
              </div>
              <Button onClick={onAddValue} className="add-option-button">
                <PlusOutlined />
                Add Option
              </Button>

              <Row className="text-warn-container">
                <Col span={2}>
                  <WarningFilled />
                </Col>
                <Col span={22}>
                  <p>
                    Saving will update the existing property. Are you sure you
                    want to proceed?
                  </p>
                </Col>
              </Row>
            </>
          )}

          <Row className="button-container">
            <Button onClick={props.onClose} size="large">
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              loading={mutation.isLoading || updateMutation.isLoading}
            >
              Save
            </Button>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default AddPropertyModal
